if (!customElements.get('discount-eligibility')) {
  class DiscountEligibility extends HTMLElement {
    static currentCode = '';
    static hasAttempted = false;
    static cartRequest = null;

    constructor() {
      super();
      this.lastSubtotal = null;
      this.refreshTimer = null;
      this.isApplying = false;
      this.abortController = new AbortController();
    }

    connectedCallback() {
      this.currency = this.dataset.currency || 'INR';
      this.couponRules = this.parseCouponRules();
      this.form = this.querySelector('[data-discount-form]');
      this.input = this.querySelector('[data-discount-input]');
      this.submitButton = this.querySelector('[data-discount-submit]');
      this.buttonText = this.querySelector('[data-discount-button-text]');
      this.spinner = this.querySelector('.loading__spinner');
      this.status = this.querySelector('[data-discount-status]');

      if (!this.form || !this.input || !this.submitButton || !this.status) return;

      this.abortController = new AbortController();
      this.input.value = DiscountEligibility.currentCode;
      this.form.addEventListener('submit', this.onSubmit.bind(this), { signal: this.abortController.signal });
      this.input.addEventListener('input', this.onInput.bind(this), { signal: this.abortController.signal });

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleRefresh());
      }

      this.scheduleRefresh();
    }

    disconnectedCallback() {
      window.clearTimeout(this.refreshTimer);
      this.abortController.abort();
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    }

    onInput(event) {
      event.target.setCustomValidity('');
      DiscountEligibility.currentCode = event.target.value;
      document.querySelectorAll('discount-eligibility [data-discount-input]').forEach((input) => {
        if (input !== event.target) input.value = event.target.value;
      });

      if (DiscountEligibility.hasAttempted && Number.isFinite(this.lastSubtotal)) {
        this.renderEligibility(this.lastSubtotal, this.getCouponRule(event.target.value));
      } else {
        this.setStatus('', '');
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.isApplying || !this.input.reportValidity()) return;

      DiscountEligibility.currentCode = this.input.value.trim();
      if (!DiscountEligibility.currentCode) {
        this.input.setCustomValidity('Enter a coupon code.');
        this.input.reportValidity();
        return;
      }

      DiscountEligibility.hasAttempted = true;
      this.setLoading(true);
      this.setStatus('Checking your coupon...', 'neutral');

      try {
        const cart = await this.fetchLatestCart();
        const subtotal = Number(cart.items_subtotal_price);
        if (!Number.isFinite(subtotal)) throw new Error('Cart subtotal was unavailable.');

        this.lastSubtotal = subtotal;
        const couponRule = this.getCouponRule(DiscountEligibility.currentCode);

        if (couponRule && subtotal < couponRule.minimum) {
          this.renderEligibility(subtotal, couponRule);
          this.setLoading(false);
          return;
        }

        const updatedCart = await this.updateCartDiscount(DiscountEligibility.currentCode);
        if (!this.isCouponApplicable(updatedCart, DiscountEligibility.currentCode)) {
          await this.removeUnavailableCoupon();
          this.setStatus('Unavailable coupon.', 'error');
          this.setLoading(false);
          return;
        }

        this.isApplying = true;
        this.setStatus('Coupon applied. Opening secure checkout...', 'success');
        window.setTimeout(() => this.openCheckout(), 450);
      } catch (error) {
        console.error('Coupon validation failed:', error);
        this.setStatus('We could not check your coupon right now. Please try again.', 'error');
        this.setLoading(false);
      }
    }

    scheduleRefresh() {
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = window.setTimeout(() => this.refreshEligibility(), 80);
    }

    async refreshEligibility() {
      try {
        const cart = await DiscountEligibility.loadCart(this.dataset.cartUrl || `${this.normalizedRootUrl()}cart.js`);
        const subtotal = Number(cart.items_subtotal_price);
        if (!Number.isFinite(subtotal)) return;

        this.lastSubtotal = subtotal;
        if (DiscountEligibility.hasAttempted && DiscountEligibility.currentCode) {
          this.renderEligibility(subtotal, this.getCouponRule(DiscountEligibility.currentCode));
        }
      } catch (error) {
        console.error('Coupon eligibility refresh failed:', error);
        if (DiscountEligibility.hasAttempted) {
          this.setStatus('We could not refresh coupon eligibility. Please try again.', 'error');
        }
      }
    }

    renderEligibility(subtotal, couponRule) {
      if (couponRule && subtotal < couponRule.minimum) {
        const remaining = couponRule.minimum - subtotal;
        this.setStatus(
          `This offer is applicable on orders above ${this.formatMoney(couponRule.minimum)}. Add ${this.formatMoney(remaining)} more to unlock this offer.`,
          'error'
        );
        return;
      }

      this.setStatus('', '');
    }

    parseCouponRules() {
      try {
        const rules = JSON.parse(this.dataset.couponRules || '{}');
        return Object.entries(rules).reduce((couponRules, [code, minimum]) => {
          const normalizedCode = this.normalizeCouponCode(code);
          const minimumAmount = Number(minimum);
          if (normalizedCode && Number.isFinite(minimumAmount) && minimumAmount >= 0) {
            couponRules[normalizedCode] = { minimum: minimumAmount };
          }
          return couponRules;
        }, {});
      } catch (error) {
        console.error('Coupon rules could not be read:', error);
        return {};
      }
    }

    getCouponRule(code) {
      return this.couponRules[this.normalizeCouponCode(code)] || null;
    }

    normalizeCouponCode(code) {
      return String(code || '').trim().toUpperCase();
    }

    fetchLatestCart() {
      const cartUrl = this.dataset.cartUrl || `${this.normalizedRootUrl()}cart.js`;
      return fetch(cartUrl, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      }).then((response) => {
        if (!response.ok) throw new Error(`Cart request failed with status ${response.status}.`);
        return response.json();
      });
    }

    updateCartDiscount(code) {
      const updateUrl = this.dataset.cartUpdateUrl || `${this.normalizedRootUrl()}cart/update.js`;
      return fetch(updateUrl, {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discount: code }),
      }).then((response) => {
        if (!response.ok) throw new Error(`Coupon request failed with status ${response.status}.`);
        return response.json();
      });
    }

    removeUnavailableCoupon() {
      return this.updateCartDiscount('').catch((error) => {
        console.error('Unavailable coupon cleanup failed:', error);
      });
    }

    openCheckout() {
      const checkoutButton = this.getCheckoutButton();

      if (checkoutButton && !checkoutButton.disabled) {
        checkoutButton.click();
        this.resetCheckoutState();
        return;
      }

      window.location.assign(`${this.normalizedRootUrl()}checkout`);
    }

    getCheckoutButton() {
      const checkoutSelector = this.dataset.checkoutSelector;

      if (checkoutSelector) {
        try {
          const checkoutButton = document.querySelector(checkoutSelector);
          if (checkoutButton) return checkoutButton;
        } catch (error) {
          console.error('Coupon checkout selector was invalid:', error);
        }
      }

      return document.querySelector(
        'magic-checkout-btn:not([page-type="product"]), button[name="checkout"], input[name="checkout"]'
      );
    }

    resetCheckoutState() {
      window.setTimeout(() => {
        this.isApplying = false;
        this.setLoading(false);
      }, 1000);
    }

    isCouponApplicable(cart, code) {
      const normalizedCode = this.normalizeCouponCode(code);
      const discountCodes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      const matchingCode = discountCodes.find(
        (discountCode) => this.normalizeCouponCode(discountCode.code) === normalizedCode
      );

      if (matchingCode) return matchingCode.applicable !== false;

      const discountApplications = [
        ...(Array.isArray(cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []),
        ...(Array.isArray(cart.items)
          ? cart.items.flatMap((item) =>
              Array.isArray(item.line_level_discount_allocations)
                ? item.line_level_discount_allocations.map((allocation) => allocation.discount_application)
                : []
            )
          : []),
      ];

      return discountApplications.some(
        (application) => application && this.normalizeCouponCode(application.title) === normalizedCode
      );
    }

    static loadCart(cartUrl) {
      if (!DiscountEligibility.cartRequest) {
        DiscountEligibility.cartRequest = fetch(cartUrl, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart request failed with status ${response.status}.`);
            return response.json();
          })
          .finally(() => {
            DiscountEligibility.cartRequest = null;
          });
      }

      return DiscountEligibility.cartRequest;
    }

    normalizedRootUrl() {
      const rootUrl = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
      return rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    }

    formatMoney(cents) {
      const hasFraction = Math.abs(cents) % 100 !== 0;
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: hasFraction ? 2 : 0,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch (error) {
        return `\u20B9${(cents / 100).toFixed(hasFraction ? 2 : 0)}`;
      }
    }

    setLoading(isLoading) {
      this.submitButton.disabled = isLoading;
      this.submitButton.setAttribute('aria-busy', String(isLoading));
      this.buttonText?.classList.toggle('hidden', isLoading);
      this.spinner?.classList.toggle('hidden', !isLoading);
    }

    setStatus(message, state) {
      this.status.textContent = message;
      if (state) {
        this.status.dataset.state = state;
      } else {
        delete this.status.dataset.state;
      }
    }
  }

  customElements.define('discount-eligibility', DiscountEligibility);
}
